import AppConfigService from 'js/services/api/app-config'

const DEFAULT_PASSWORD_POLICY = {
  source: "moverick",
  available: true,
  minimum_length: 8,
  maximum_length: null,
  minimum_digits: 0,
  minimum_lowercase: 0,
  minimum_uppercase: 0,
  minimum_special_characters: 0,
  not_username: false,
  not_email: true,
  unsupported_rules: [],
  server_validation_required: false
}

export default ({
  namespaced: true,

  state: {
    currentAppConfig: null, // Estado inicial como null para identificar que aún no ha cargado
    basicDataLoading: false,
    basicDataLoaded: false
  },
  mutations: {
    setAppConfig(state, appConfig) {
      state.currentAppConfig = appConfig
    },
    setBasicDataLoading(state, value) {
      state.basicDataLoading = value
    },
    setBasicDataLoaded(state, value) {
      state.basicDataLoaded = value
    }
  },
  getters: {
    /**
     * Returns the config object
     * @returns {Object}
     */
    currentAppConfig(state) {
      return state.currentAppConfig || {} // Devuelve un objeto vacío si aún no está disponible
    },

    /**
     * Returns the effective registration workflow.
     * Only email_verification opts in; every other value keeps legacy behavior.
     * @returns {String}
     */
    effectiveRegistrationPath(state, getters) {
      return getters.currentAppConfig?.registration_path === 'email_verification'
        ? 'email_verification'
        : 'legacy'
    },

    /**
     * Returns true if app config is stored
     * @returns {Boolean}
     */
    hasAppConfig(state, getters) {
      return !_.isEmpty(getters.currentAppConfig) // Indica si ya hay configuración cargada
    },

    /**
     * Returns true if client is configured to use keycloak
     * @returns {Boolean}
     */
    // eslint-disable-next-line no-unused-vars
    usingKeyCloak(state, getters) {
      return state?.currentAppConfig?.session_manager === 'keycloak'
    },

    /**
     * The code of the current client
     * Useful for debugging but should NOT be used fo logic.
     * Custom logic should be used in personalized at component level
     * Returns an empty string if no client loaded
     * @returns {String}
     */
    // eslint-disable-next-line no-unused-vars
    appCode(state, getters) {
      return state?.currentAppConfig.code || ''
    },

    /**
     * Returns true if multiple municipios available (Should load cities selector)
     * @returns {Boolean}
     */
    hasMunicipalities(state, getters) {
      return getters.municipality_ids.length > 1
    },

    /**
     * Return the array of munipality ids available for the client
     * Returns an empty array if not present
     * @returns {Array}
     */
    municipality_ids(state, getters) {
      return getters.currentAppConfig?.municipality_ids || []
    },

    /**
     * Returns true if only has a single admin area
     * @returns {Boolean}
     */
    hasSingleMunicipality(state, getters) {
      return (getters.currentAppConfig?.municipality_ids || []).length === 1
    },

    /**
     * Returns true if the minimum data is loaded in Vuex
     * @returns {Boolean}
     */
    hasBasicData(getters, rootGetters) {
      const hasAppConfig = getters.hasAppConfig
      const hasSingleMunicipality = getters.hasSingleMunicipality
      const municipalityLoaded = rootGetters['municipality/municipalityLoaded']
      // devDump('hasBasicData::hasAppConfig', hasAppConfig)
      // devDump('hasBasicData::hasSingleMunicipality', hasSingleMunicipality)
      // devDump('hasBasicData::municipalityLoaded', municipalityLoaded)
      if (hasAppConfig) return false
      if (hasSingleMunicipality) {
        // If only 1 municipality it should be loaded
        return municipalityLoaded
      }

      // City selector should load
      return true
    },

    planner_router_url(state) {
      const url = state.currentAppConfig.planner_router_url
      if(!url.includes('/api')) {
        url += '/api/'
      }
      return url
    },

    planner_router_reverse_url(state) {
      return state.currentAppConfig.planner_router_url.replace(/\/api\/?/, '')
    },

    getPasswordPolicy(state) {
      // Keep the normalized policy object intact: 0, false and null are
      // meaningful values for individual password-policy rules.
      return state?.currentAppConfig?.password_policy || DEFAULT_PASSWORD_POLICY
    }
  },

  actions: {
    /**
     * Launches a series of ajax requests to the API in order to obtain the data for
     * 1. Client configuration
     * 2. Administrative Area
     * 3. In the case that only a single municipality is available it will be loaded
     * 4. Tries to load the user if logged in
     * @returns
     */
    async loadBasicData({state, commit, getters, dispatch, rootGetters}, options) {
      if (state.basicDataLoading || state.basicDataLoaded) return
      commit('setBasicDataLoading', true)
      try {
        const currentAppConfigLoaded = getters['appConfig/currentAppConfig']
        const administrativeAreaLoaded = rootGetters['administrativeArea/hasAdministrativeArea']
        const hasSingleMunicipality = getters.hasSingleMunicipality
        const municipalitiesLoaded = rootGetters['municipality/municipalityLoaded']
        const currentUser = rootGetters.currentUser
        const jwt = Vue.auth.token()

        if (!currentAppConfigLoaded) {
          await dispatch('appConfig/loadAppConfig', null, {root: true})
        }

        if (!administrativeAreaLoaded) {
          await dispatch('administrativeArea/loadAdministrativeAreaFromMunicipality',
            null, {root: true})
          await dispatch('municipality/loadAvailableMunicipalities', { routeMunicipalityName: options?.routeMunicipalityName }, {root: true})
        }

        if (hasSingleMunicipality && !municipalitiesLoaded) {
          await dispatch('municipality/loadAvailableMunicipalities', null, {root: true})
        }

        commit('setBasicDataLoaded', true)

        if (_.isEmpty(currentUser) && !_.isEmpty(jwt)) {
          await dispatch('loadCurrentUser', null, {root: true})
        }
      } finally {
        commit('setBasicDataLoading', false)
      }
    },

    /** @todo API calls should ONLY exist in VUEX */
    async loadAppConfig({commit}) {
      try {
        const appConfig = await AppConfigService.current() // Llama al servicio
        commit('setAppConfig', appConfig) // Guarda en el estado
      } catch (error) {
        console.error('Error al cargar la configuración de la app:', error)
        commit('setAppConfig', {}) // Asegura que no sea null
      }
    }
  }
})
